// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Drawn shapes where each corner decides for itself whether it is a corner.
 *
 * Almost nothing on a site is straight. A haul route curves around the
 * building, silt fence follows a contour, a crane swing is an arc. But almost
 * nothing is *entirely* curved either: a fence line runs dead straight down
 * the property line, turns a hard 90 at the corner, then sweeps around the
 * gate. A switch that bends the whole shape at once cannot draw that, and
 * bending the straight runs to get one smooth corner is a worse drawing than
 * the one you started with.
 *
 * So smoothness lives on the vertex, not on the shape. Each vertex is a corner
 * or a smooth point; a span between two corners is drawn as a straight line,
 * and a span touching a smooth point bends only on that side. Right-click a
 * vertex and only that vertex changes.
 *
 * The curve through a smooth point is the Catmull-Rom tangent, which passes
 * *through* the vertex. A B-spline would be smoother and would miss every
 * point the person placed, which on a drawing representing real ground is the
 * wrong trade.
 */

export interface Point {
  x: number;
  y: number;
  /** Smooth through this vertex instead of turning a corner at it. */
  curve?: boolean;
  /**
   * Bezier handles, as offsets from the vertex itself.
   *
   * Absent means "work it out from the neighbours", which is what every
   * curve drawn before this does and what a newly curved vertex still does.
   * Present means somebody dragged it, and their shape wins: a curve you
   * cannot reshape by hand is one that comes out nearly right and cannot be
   * finished. Offsets rather than positions, so moving, scaling or rotating
   * the shape carries them without touching them.
   */
  hIn?: { x: number; y: number };
  hOut?: { x: number; y: number };
}

/** How much the curve bows between vertices. 1 is the usual Catmull-Rom. */
export const DEFAULT_TENSION = 1;

const fmt = (n: number) => (Math.round(n * 100) / 100).toString();

/** A straight-segment path, closed back to the start if asked. */
export function straightPath(points: Point[], closed = false): string {
  if (!points || points.length === 0) return "";
  const head = `M ${fmt(points[0].x)} ${fmt(points[0].y)}`;
  const rest = points.slice(1).map((p) => `L ${fmt(p.x)} ${fmt(p.y)}`).join(" ");
  return [head, rest, closed && points.length > 2 ? "Z" : ""].filter(Boolean).join(" ");
}

/**
 * The path for a shape, bending only where its vertices say to.
 *
 * Each span asks its two ends what they are. A corner holds its control point
 * on itself, so the line leaves straight; a smooth point offsets its control
 * along the Catmull-Rom tangent, so the line leaves curving. A span between
 * two corners is emitted as a plain line rather than a Bezier that happens to
 * be flat, which keeps the output readable and the file smaller.
 */
/** The handle leaving a vertex: the one that was dragged, or the one the
 *  neighbours imply. */
export function outHandle(p0: Point, p1: Point, p2: Point, tension = DEFAULT_TENSION) {
  if (p1.hOut) return { ...p1.hOut };
  return { x: ((p2.x - p0.x) / 6) * tension, y: ((p2.y - p0.y) / 6) * tension };
}

/** And the one arriving at it. */
export function inHandle(p1: Point, p2: Point, p3: Point, tension = DEFAULT_TENSION) {
  if (p2.hIn) return { ...p2.hIn };
  return { x: -((p3.x - p1.x) / 6) * tension, y: -((p3.y - p1.y) / 6) * tension };
}

/** Where a vertex's two handles sit, whether dragged or implied. */
export function handlesOf(
  points: Point[], index: number, closed = false, tension = DEFAULT_TENSION,
): { in: { x: number; y: number }; out: { x: number; y: number } } | null {
  const n = (points || []).length;
  if (!n || index < 0 || index >= n) return null;
  const at = (i: number): Point => (closed
    ? points[((i % n) + n) % n]
    : points[Math.max(0, Math.min(n - 1, i))]);
  return {
    out: outHandle(at(index - 1), at(index), at(index + 1), tension),
    in: inHandle(at(index - 1), at(index), at(index + 1), tension),
  };
}

/**
 * A handle moved.
 *
 * The other side follows, equal and opposite, because a curved vertex is
 * meant to be smooth and a dragged handle that leaves a kink behind turns a
 * vertex into a corner nobody asked for. Passing mirror: false is how a
 * deliberate kink gets made.
 */
export function withHandle(
  points: Point[], index: number, which: "in" | "out",
  offset: { x: number; y: number }, mirror = true,
): Point[] {
  const pts = points || [];
  if (index < 0 || index >= pts.length) return pts;
  const other = which === "in" ? "hOut" : "hIn";
  const mine = which === "in" ? "hIn" : "hOut";
  return pts.map((p, i) => (i !== index ? p : {
    ...p,
    curve: true,
    [mine]: { x: offset.x, y: offset.y },
    ...(mirror ? { [other]: { x: -offset.x, y: -offset.y } } : {}),
  } as Point));
}

/** A vertex handed back to the shape of its neighbours. */
export function clearHandles(points: Point[], index: number): Point[] {
  return (points || []).map((p, i) => {
    if (i !== index) return p;
    const { hIn: _a, hOut: _b, ...rest } = p;
    return rest as Point;
  });
}

export function pathFromPoints(points: Point[], closed = false, tension = DEFAULT_TENSION): string {
  if (!points || points.length === 0) return "";
  if (points.length === 1) return `M ${fmt(points[0].x)} ${fmt(points[0].y)}`;
  if (points.length === 2) return straightPath(points, false);

  const n = points.length;
  const at = (i: number): Point => (closed
    ? points[((i % n) + n) % n]
    : points[Math.max(0, Math.min(n - 1, i))]);

  const last = closed ? n : n - 1;
  const out = [`M ${fmt(points[0].x)} ${fmt(points[0].y)}`];
  for (let i = 0; i < last; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    if (!p1.curve && !p2.curve) {
      out.push(`L ${fmt(p2.x)} ${fmt(p2.y)}`);
      continue;
    }
    const h1 = p1.curve ? outHandle(p0, p1, p2, tension) : null;
    const h2 = p2.curve ? inHandle(p1, p2, p3, tension) : null;
    const c1 = h1 ? { x: p1.x + h1.x, y: p1.y + h1.y } : p1;
    const c2 = h2 ? { x: p2.x + h2.x, y: p2.y + h2.y } : p2;
    out.push(`C ${fmt(c1.x)} ${fmt(c1.y)}, ${fmt(c2.x)} ${fmt(c2.y)}, ${fmt(p2.x)} ${fmt(p2.y)}`);
  }
  if (closed) out.push("Z");
  return out.join(" ");
}

/** Flips one vertex between a corner and a smooth point. */
export function toggleVertexCurve(points: Point[], index: number): Point[] {
  if (!points || index < 0 || index >= points.length) return points;
  return points.map((p, i) => (i === index ? { ...p, curve: !p.curve } : p));
}

/** Makes every vertex a corner, or every vertex smooth. */
export function setAllCurve(points: Point[], curve: boolean): Point[] {
  return (points || []).map((p) => ({ ...p, curve }));
}

/** How many vertices are smooth, for a panel that wants to say so. */
export function curvedCount(points: Point[]): number {
  return (points || []).filter((p) => p.curve).length;
}

/**
 * Vertices read from a shape saved with the old whole-shape switch.
 *
 * That switch said "all of them or none of them", so it maps onto the vertex
 * flags exactly. Applied only when no vertex carries its own flag yet, so a
 * shape edited since is never dragged back to the old all-or-nothing answer.
 */
export function withLegacyCurve(points: Point[], legacyCurved?: boolean): Point[] {
  if (!points || !points.length) return points || [];
  if (!legacyCurved) return points;
  if (points.some((p) => p.curve !== undefined)) return points;
  return setAllCurve(points, true);
}

// ── Vertices ─────────────────────────────────────────────────────────────────

/** Fewest vertices a shape can keep and still be that shape. */
export function minVertices(closed: boolean): number {
  return closed ? 3 : 2;
}

/**
 * Where a new vertex would go if the person clicked here.
 *
 * Returns the span clicked and how far along it the click fell, so the caller
 * can drop the vertex exactly under the pointer instead of at the midpoint —
 * clicking a line and having the point appear somewhere else is the thing
 * that makes this feel broken.
 */
export function nearestSegment(
  points: Point[],
  p: Point,
  closed = false,
): { index: number; point: Point; dist: number } | null {
  if (!points || points.length < 2) return null;
  const spans = closed ? points.length : points.length - 1;

  let best: { index: number; point: Point; dist: number } | null = null;
  for (let i = 0; i < spans; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    const proj = { x: a.x + dx * t, y: a.y + dy * t };
    const dist = Math.hypot(p.x - proj.x, p.y - proj.y);
    if (!best || dist < best.dist) best = { index: i, point: proj, dist };
  }
  return best;
}

/** A vertex added after `index`, which is how a span is split in two. */
export function insertVertexAfter(points: Point[], index: number, p: Point): Point[] {
  const at = Math.max(0, Math.min(points.length - 1, index));
  return [...points.slice(0, at + 1), { ...p }, ...points.slice(at + 1)];
}

/**
 * A vertex taken out, or the shape unchanged when taking it out would stop it
 * being a shape. Refusing is better than silently leaving a two-point
 * "polygon" that renders as a line.
 */
export function removeVertex(points: Point[], index: number, closed = false): Point[] {
  if (!points || index < 0 || index >= points.length) return points;
  if (points.length <= minVertices(closed)) return points;
  return points.filter((_, i) => i !== index);
}

/** Whether a vertex can be removed, for greying out the control that does it. */
export function canRemoveVertex(points: Point[], closed = false): boolean {
  return !!points && points.length > minVertices(closed);
}

/**
 * The midpoint of every span, where the "add a vertex here" handles sit.
 *
 * Midpoints of the straight spans even on a curved shape: the handles mark
 * which span a new vertex joins, and chasing them along the bowed curve would
 * put them where the span they belong to is hardest to identify.
 */
export function spanMidpoints(points: Point[], closed = false): Array<{ index: number; point: Point }> {
  if (!points || points.length < 2) return [];
  const spans = closed ? points.length : points.length - 1;
  const out: Array<{ index: number; point: Point }> = [];
  for (let i = 0; i < spans; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    out.push({ index: i, point: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } });
  }
  return out;
}

/**
 * Vertices thinned out of a freehand run.
 *
 * Drawing by hand leaves hundreds of points a pixel apart, which are slow to
 * render, impossible to edit by vertex and meaningless as a record of where
 * anything is. Ramer–Douglas–Peucker keeps the ones that carry the shape.
 *
 * Near-linear on a drawn stroke, which wanders: 20,000 points thin in about
 * 30ms. Degrades to quadratic only when every single point is a local peak —
 * a pixel-perfect sawtooth — which a hand does not produce. Iterative rather
 * than recursive so a deeply split run cannot overflow the stack.
 */
export function simplify(points: Point[], tolerance: number): Point[] {
  if (!points || points.length <= 2 || tolerance <= 0) return points || [];

  const perp = (p: Point, a: Point, b: Point): number => {
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
  };

  const keep = new Array(points.length).fill(false);
  keep[0] = keep[points.length - 1] = true;

  // Iterative rather than recursive: a long freehand stroke would otherwise
  // be able to blow the stack on the drawing's own input.
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let worst = 0, at = -1;
    for (let i = first + 1; i < last; i++) {
      const d = perp(points[i], points[first], points[last]);
      if (d > worst) { worst = d; at = i; }
    }
    if (at >= 0 && worst > tolerance) {
      keep[at] = true;
      stack.push([first, at], [at, last]);
    }
  }
  return points.filter((_, i) => keep[i]);
}
